export interface ChatTheme {
  colors?: Partial<Record<'primary' | 'background' | 'surface' | 'text' | 'userBubble' | 'assistantBubble', string>>;
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'full' | number;
  /** `auto` follows the visitor's `prefers-color-scheme`. */
  mode?: 'light' | 'dark' | 'auto';
  /** Max width of interactive cards: px number or a CSS length (`24rem`, `80%`). */
  cardMaxWidth?: number | string;
}
export interface Variants { buttons?: 'chips' | 'list'; carousel?: 'slider' | 'list' }
const LIGHT = { primary: '#4f46e5', background: '#ffffff', surface: '#f4f4f5', text: '#18181b', userBubble: '#4f46e5', assistantBubble: '#f4f4f5' };
const DARK = { primary: '#6366f1', background: '#0a0a12', surface: '#1c1c27', text: '#f4f4f5', userBubble: '#6366f1', assistantBubble: '#1f1f2b' };
const RADII = { none: '0px', sm: '6px', md: '10px', lg: '16px', full: '9999px' };
const VAR = { primary: '--osvi-color-primary', background: '--osvi-color-background', surface: '--osvi-color-surface', text: '--osvi-color-text', userBubble: '--osvi-color-user-bubble', assistantBubble: '--osvi-color-assistant-bubble' };
export const cssVar = (key: keyof typeof LIGHT): string => `var(${VAR[key]}, ${LIGHT[key]})`;
const SCHEME = '(prefers-color-scheme: dark)';
const schemeQuery = (): MediaQueryList | null => typeof matchMedia === 'function' ? matchMedia(SCHEME) : null;
/** `auto` resolves to the visitor's colour scheme; light when the browser cannot say. */
export const resolvesDark = (mode: ChatTheme['mode']): boolean => mode === 'dark' || mode === 'auto' && !!schemeQuery()?.matches;
/** Calls `onChange` when the visitor's colour scheme flips; returns the unsubscribe. */
export function watchColorScheme(onChange: () => void): () => void {
  const query = schemeQuery(); query?.addEventListener?.('change', onChange);
  return () => query?.removeEventListener?.('change', onChange);
}
export function themeVariables(theme: ChatTheme | undefined): Record<string, string> {
  const colors = { ...(resolvesDark(theme?.mode) ? DARK : LIGHT), ...theme?.colors };
  // The user's bubble is brand-coloured unless the customer picks it separately.
  if (!theme?.colors?.userBubble && theme?.colors?.primary) colors.userBubble = theme.colors.primary;
  const out: Record<string, string> = {};
  for (const key of Object.keys(VAR) as (keyof typeof LIGHT)[]) out[VAR[key]] = colors[key];
  const radius = theme?.radius ?? 'md';
  out['--osvi-radius'] = typeof radius === 'number' ? `${radius}px` : RADII[radius];
  const cardMaxWidth = cardWidth(theme?.cardMaxWidth);
  if (cardMaxWidth) out['--osvi-card-max-width'] = cardMaxWidth;
  return out;
}
function cardWidth(value: number | string | undefined): string | undefined {
  if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? `${value}px` : undefined;
  return typeof value === 'string' && /^\d+(\.\d+)?(px|rem|em|%|vw)$/.test(value) ? value : undefined;
}
export function explicitThemeVariables(theme: ChatTheme | undefined): Record<string, string> {
  if (!theme) return {};
  if (theme.mode) return themeVariables(theme);
  const full = themeVariables(theme), out: Record<string, string> = {};
  for (const key of Object.keys(theme.colors ?? {}) as (keyof typeof LIGHT)[]) out[VAR[key]] = full[VAR[key]]!;
  if (theme.radius !== undefined) out['--osvi-radius'] = full['--osvi-radius']!;
  if (full['--osvi-card-max-width']) out['--osvi-card-max-width'] = full['--osvi-card-max-width'];
  return out;
}
const applied = new WeakMap<HTMLElement, string[]>();
export function applyTheme(host: HTMLElement, theme: ChatTheme | undefined): void {
  for (const key of applied.get(host) ?? []) host.style.removeProperty(key);
  const variables = explicitThemeVariables(theme);
  for (const [key, value] of Object.entries(variables)) host.style.setProperty(key, value);
  applied.set(host, Object.keys(variables));
}
